import type { EventData, PaginatedEventsResponse, GetEventsOptions } from "@/types/events";

const baseURL = (import.meta.env.VITE_API_BASEURL || "http://localhost:8000").replace(/\/+$/, "");
const eventsURL = `${baseURL}/v1/events`;

interface ApiEvent {
  id: string;
  title: string;
  description: string;
  full_description?: string;
  image_url?: string;
  agenda?: Array<{ time?: string; title: string; description?: string }>;
  prerequisites?: string[];
  what_you_will_learn?: string[];
  date: string;
  time: string;
  location?: string;
  attendees: number;
  badge?: string;
  is_featured?: boolean;
  external_registration_link?: string;
  type: string;
  speakers?: Array<{ name: string; image_url?: string; bio?: string }>;
}

const mapEvent = (event: ApiEvent): EventData => ({
  id: event.id,
  title: event.title,
  description: event.description,
  fullDescription: event.full_description || event.description,
  imageURL: event.image_url,
  agenda: event.agenda
    ?.map(({ time, title, description }) => [time, title, description].filter(Boolean).join(" - "))
    .join("\n"),
  prerequisites: event.prerequisites?.join("\n"),
  whatYoullLearn: event.what_you_will_learn?.join("\n"),
  date: event.date,
  time: event.time,
  location: event.location,
  attendees: event.attendees,
  badge: event.badge || (event.is_featured ? "Featured" : undefined),
  GoogleFormURL: event.external_registration_link,
  type: { en: event.type, ar: event.type },
  speaker: event.speakers?.[0]
    ? {
        name: event.speakers[0].name,
        image: event.speakers[0].image_url,
        bio: event.speakers[0].bio,
      }
    : null,
});

export const getEvents = async(
  options: GetEventsOptions = {}
): Promise<PaginatedEventsResponse> => {
  const {
    language = "en",
    page = 1,
    pageSize = 10,
    filters = {}
  } = options;

  // Build query parameters
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
    ...(filters.past && { past: "true" }),
  });

  const res = await fetch(`${eventsURL}?${params.toString()}`, {
    headers: {
      Accept: "application/json",
      "Accept-Language": language,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch events (${res.status})`);
  }

  const data = await res.json();

  const upcomingEvents = [
    ...(data.featuredEvents ?? []),
    ...(data.regularEvents ?? []),
  ] as ApiEvent[];
  const responseEvents = (data.data ?? upcomingEvents) as ApiEvent[];
  const events = filters.featured
    ? responseEvents.filter((event) => event.is_featured)
    : responseEvents;

  return {
    data: events.map(mapEvent),
    pagination: data.pagination,
    filters: data.filters ?? {
      upcoming: !filters.past,
      featured: Boolean(filters.featured),
      past: Boolean(filters.past),
    },
  };
}

export const getEventById = async (
  id: string,
  language: "en" | "ar" = "en"
): Promise<EventData> => {
  const res = await fetch(`${eventsURL}/${encodeURIComponent(id)}`, {
    headers: {
      Accept: "application/json",
      "Accept-Language": language,
    },
  });

  if (res.status === 404) {
    throw new Error("Event not found")
  }
  
  if (!res.ok) {
    throw new Error(`Failed to fetch event (${res.status})`)
  }

  const data = await res.json() as ApiEvent

  return mapEvent(data)
}
